import { useState } from "react";
import type { ApiPick, ApiPlayerDetails } from "../../../api";
import { PICK_COUNT } from "../../../constants/game.ts";

export function usePlayerSelectionForm(
  picks: ApiPick[],
  players: ApiPlayerDetails[],
) {
  const [selectedPlayers, setSelectedPlayers] = useState<ApiPlayerDetails[]>(
    () => {
      const initiallySelectedPlayers = players.filter((player) =>
        picks.some((pick) => pick.playerId === player.id),
      );

      return initiallySelectedPlayers.slice(0, PICK_COUNT);
    },
  );

  const addPlayer = (player: ApiPlayerDetails) => {
    setSelectedPlayers((prev) => {
      const currentCount = prev.length;
      if (currentCount >= PICK_COUNT) {
        console.warn(
          `Cannot add player ${player.firstName} ${player.lastName}. Maximum of ${PICK_COUNT} players already selected.`,
        );
        return prev;
      }

      if (prev.some((p) => p.id === player.id)) {
        console.warn(
          `Player ${player.firstName} ${player.lastName} is already selected.`,
        );
        return prev;
      }

      return [...prev, player];
    });
  };

  const removePlayer = (playerId: string) => {
    setSelectedPlayers((prev) => {
      if (!prev.some((player) => player.id === playerId)) {
        console.warn(`Player with ID ${playerId} is not selected.`);
        return prev;
      }

      return prev.filter((player) => player.id !== playerId);
    });
  };

  const randomizeSelections = (players: ApiPlayerDetails[]) => {
    const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
    const selected = shuffledPlayers.slice(0, PICK_COUNT);
    setSelectedPlayers(selected);
  };

  const isPlayerSelected = (playerId: string) =>
    selectedPlayers.some((player) => player.id === playerId);

  return {
    randomizeSelections,
    selectedPlayers,
    addPlayer,
    removePlayer,
    isPlayerSelected,
  };
}
